<template>
  <v-dialog
    :model-value="dialog"
    @update:model-value="$emit('update:dialog', $event)"
    max-width="600px"
    persistent
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">{{ isEditMode ? "Edit Folder" : "Add Folder" }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          @click="$emit('update:dialog', false)"
        ></v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-4">
        <v-form ref="form" @submit.prevent="onSubmit">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="folderName.value.value"
                :error-messages="folderName.errorMessage.value"
                label="Folder Name"
                variant="outlined"
                density="compact"
                required
                autocomplete="off"
              ></v-text-field>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="rootFolder.value.value"
                :error-messages="rootFolder.errorMessage.value"
                :items="rootFolderOptions"
                item-title="name"
                item-value="id"
                label="Root Folder"
                variant="outlined"
                density="compact"
                clearable
                :disabled="isEditMode"
                :hint="rootFolderOptions.length === 0 ? 'No folders available. This will be a root folder.' : 'Select a parent folder or leave empty for root'"
                persistent-hint
              >
                <template v-slot:prepend-item v-if="rootFolderOptions.length > 0">
                  <v-list-item
                    title="None (Root)"
                    value="null"
                    @click="rootFolder.value.value = null"
                  >
                    <template v-slot:prepend>
                      <v-icon>mdi-folder-outline</v-icon>
                    </template>
                  </v-list-item>
                  <v-divider></v-divider>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          variant="text"
          @click="$emit('update:dialog', false)"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="onSubmit"
          :loading="loading"
        >
          {{ isEditMode ? "Update" : "Save" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import { saveCmsFolder, updateCmsFolder } from "@/store/api";
import Axios from "axios";

export default {
  name: "cmsFolderModal",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    folderData: {
      type: Object,
      default: null,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    foldersList: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:dialog", "refresh"],
  setup(props, { emit }) {
    const form = ref(null);
    const loading = ref(false);

    const validationSchema = yup.object({
      folderName: yup.string().required("Folder name is required").max(255, "Folder name must be less than 255 characters"),
      rootFolder: yup.mixed().nullable(),
    });

    const { handleSubmit, resetForm } = useForm({
      validationSchema,
    });

    const folderName = useField("folderName");
    const rootFolder = useField("rootFolder");

    // Filter out the current folder from root folder options when editing
    const rootFolderOptions = computed(() => {
      if (props.isEditMode && props.folderData) {
        return props.foldersList.filter(
          (folder) => folder.id !== props.folderData.id
        );
      }
      return props.foldersList;
    });

    watch(
      () => props.folderData,
      (newData) => {
        if (newData && props.isEditMode) {
          folderName.value.value = newData.name || "";
          rootFolder.value.value = newData.parent_id || null;
        } else {
          folderName.value.value = "";
          rootFolder.value.value = null;
        }
      },
      { immediate: true }
    );

    // Reset form when dialog closes
    watch(
      () => props.dialog,
      (isOpen) => {
        if (!isOpen) {
          resetForm();
        } else if (props.folderData && props.isEditMode) {
          folderName.value.value = props.folderData.name || "";
          rootFolder.value.value = props.folderData.parent_id || null;
        } else {
          folderName.value.value = "";
          rootFolder.value.value = null;
        }
      }
    );

    const onSubmit = handleSubmit(async (values) => {
      loading.value = true;
      try {
        const payload = {
          name: values.folderName,
          parent_id: values.rootFolder || null,
        };

        let response;
        if (props.isEditMode && props.folderData) {
          response = await Axios.put(
            `${updateCmsFolder}/${props.folderData.id}`,
            payload
          );
        } else {
          response = await Axios.post(saveCmsFolder, payload);
        }

        if (response.data.success) {
          toast.success(
            `Folder ${props.isEditMode ? "updated" : "created"} successfully`
          );
          emit("refresh");
          emit("update:dialog", false);
          resetForm();
        } else {
          toast.error(
            response.data.message || `Failed to ${props.isEditMode ? "update" : "create"} folder`
          );
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            `Error ${props.isEditMode ? "updating" : "creating"} folder`
        );
      } finally {
        loading.value = false;
      }
    });

    return {
      form,
      loading,
      folderName,
      rootFolder,
      rootFolderOptions,
      onSubmit,
      resetForm,
    };
  },
};
</script>

<style scoped>
</style>
